'use client'

import { useEffect } from 'react'
import { calendlyEmbedUrl } from '@/lib/site'

const MEETING_LINK = 'a[href*="calendly-meeting"], a[href*="reunion-de-calendly"], a[href*="calendly.com/"]'

/**
 * Injects the Calendly document prefetch on user intent (hover, focus, or
 * touch on a scheduling link) instead of eagerly on page load. Keeps the
 * embed warm for real navigations without fetching a third-party document
 * — and its cookies — for visitors who never open the scheduler.
 */
export function CalendlyPrefetchIntent() {
  useEffect(() => {
    const warm = (e: Event) => {
      const anchor = (e.target as Element | null)?.closest?.(MEETING_LINK)
      if (!anchor) return
      // A link to the scheduling page may pick the event and campaign tags (?event=demo&utm_…).
      const target = new URL(anchor.getAttribute('href') ?? '', window.location.href)
      const search = target.origin === window.location.origin ? target.search : ''
      const link = document.createElement('link')
      link.rel = 'prefetch'
      link.as = 'document'
      // The embed in the current theme, the one the scheduler page will load.
      link.href = calendlyEmbedUrl(
        document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light',
        search,
      )
      document.head.appendChild(link)
      for (const type of ['pointerover', 'focusin', 'touchstart']) {
        document.removeEventListener(type, warm)
      }
    }
    for (const type of ['pointerover', 'focusin', 'touchstart']) {
      document.addEventListener(type, warm, { passive: true })
    }
    return () => {
      for (const type of ['pointerover', 'focusin', 'touchstart']) {
        document.removeEventListener(type, warm)
      }
    }
  }, [])
  return null
}
